// Pass 74 — authenticated AI review API client. Every AI review MUTATION carries the caller's Firebase ID
// token (Authorization: Bearer …); the server derives the actor identity from the verified token, never from
// the request body. This is the ONE place that attaches auth for AI review calls (no per-component copies).
import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function assertApiBaseUrl() {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL is not configured.');
  }
}

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) {
    // 401-equivalent surfaced locally: no session → do not send an unauthenticated mutation.
    const err = new Error('You must be signed in to use AI review.');
    err.code = 'unauthenticated';
    throw err;
  }
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

async function getApiError(response, fallbackMessage) {
  try {
    const payload = await response.json();
    if (payload?.detail) return payload.detail;
  } catch (error) {
    // ignore parse failure; fall back to the default message
  }
  return fallbackMessage;
}

// Truthful mapping of transport status → caller-facing error (§6): 401 auth, 403 permission, 409 conflict/stale.
async function postJson(path, { method = 'POST', body } = {}) {
  assertApiBaseUrl();
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const message = await getApiError(response, `AI review request failed with status ${response.status}`);
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function generateAiReviewDraft(videoId, { regenerate = false } = {}) {
  return postJson(`/ai/reviews/${videoId}/generate`, { body: { regenerate } });
}

export async function editAiReviewDraft(videoId, fields) {
  return postJson(`/ai/reviews/${videoId}/draft`, { method: 'PATCH', body: fields });
}

export async function approveAiReviewDraft(videoId) {
  return postJson(`/ai/reviews/${videoId}/approve`, { body: {} });
}

// §10 — deliberately exclude an approved AI draft ("Complete Without AI"); preserves content, clears approval.
export async function unapproveAiReview(videoId) {
  return postJson(`/ai/reviews/${videoId}/unapprove`, { body: {} });
}

export async function publishAiReview(videoId) {
  return postJson(`/ai/reviews/${videoId}/publish`, { body: {} });
}
